import { useParams, Link } from "react-router";
import { ArrowLeft, Activity, Target, Wallet, List } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAppDetail, getCreditUsage } from "@/api";
import type { AppDetail, UsageLog } from "@/api";
import { toast } from "sonner";

import { DataTableStateRow } from "../components/data-table/DataTableStateRow";
import { DataTableLoadingOverlay } from "../components/data-table/DataTableLoadingOverlay";
import { TablePaginationFooter } from "../components/data-table/TablePaginationFooter";

const USAGE_PAGE_SIZE = 10;

/** Unix 秒 → "YYYY-MM-DD HH:mm:ss" */
function formatLogDateTime(unixSec: number): string {
  if (!unixSec || !Number.isFinite(unixSec)) return "—";
  const d = new Date(unixSec * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatOptional30dCount(n: number | undefined): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return "—";
  return Math.trunc(n).toLocaleString();
}

/**
 * 近 30 天 USDT 扣费展示：全为 0 时固定 `0.00`；大于 0 时去掉小数尾随 0（如 2.01000 → 2.01）；
 * 整数部分按当前环境加千分位。
 */
function formatUsdt30dStatDisplay(apiUsdt: string): string {
  const s = apiUsdt.trim();
  if (!/^\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n) || n === 0) return "0.00";
    return n.toLocaleString(undefined, { useGrouping: true, maximumFractionDigits: 20 });
  }
  const [intPart, frac = ""] = s.split(".");
  const isAllZero = intPart === "0" && (frac === "" || /^0+$/.test(frac));
  if (isAllZero) return "0.00";
  const fracTrimmed = frac.replace(/0+$/, "");
  const intWithSep = Number(intPart).toLocaleString(undefined, { useGrouping: true });
  if (!fracTrimmed) return intWithSep;
  return `${intWithSep}.${fracTrimmed}`;
}

export function KeyDetails() {
  const { t } = useTranslation();
  const { id } = useParams();

  const [appInfo, setAppInfo] = useState<AppDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [usagePage, setUsagePage] = useState(1);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usageLoading, setUsageLoading] = useState(false);

  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const loadApp = async () => {
      setIsLoading(true);
      try {
        const appResult = await getAppDetail(id);
        if (cancelled) return;
        if (appResult.ok) {
          setAppInfo(appResult.data);
        } else {
          setAppInfo(null);
          toast.error(tRef.current("common.fetchFailed"));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("[KeyDetails] Failed to load app:", error);
          setAppInfo(null);
          toast.error(tRef.current("common.loadFailed"));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadApp();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setUsagePage(1);
    setUsageLogs([]);
    setUsageTotal(0);
  }, [id]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const loadUsage = async () => {
      setUsageLoading(true);
      try {
        const res = await getCreditUsage({
          page: usagePage,
          limit: USAGE_PAGE_SIZE,
          appId: id,
        });
        if (cancelled) return;
        if (res.ok) {
          setUsageLogs(res.data.logs ?? []);
          setUsageTotal(res.data.total ?? 0);
        } else {
          setUsageLogs([]);
          setUsageTotal(0);
          console.error("[KeyDetails] getCreditUsage failed:", res.error);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("[KeyDetails] Failed to load usage logs:", error);
          setUsageLogs([]);
          setUsageTotal(0);
          toast.error(tRef.current("common.loadFailed"));
        }
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    };

    void loadUsage();
    return () => {
      cancelled = true;
    };
  }, [id, usagePage]);

  const usageTotalPages = Math.max(1, Math.ceil(usageTotal / USAGE_PAGE_SIZE));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 dark:border-[#00FFA3] mx-auto mb-4"></div>
          <p className="text-slate-500 dark:text-slate-400">
            {t("common.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (!appInfo) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">
          {t("common.notFound")}
        </p>
        <Link
          to="/keys"
          className="mt-4 inline-block text-emerald-600 dark:text-[#00FFA3] hover:underline"
        >
          {t("common.backToList")}
        </Link>
      </div>
    );
  }

  const stats = appInfo.stats30d;
  const credits30d = stats.creditsCharged30d ?? 0;
  const usdt30d = stats.usdtDeducted30d ?? "—";
  const requests30d = formatOptional30dCount(stats.totalCalls30d);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
        <Link
          to="/keys"
          className="p-2 -ml-2 text-slate-400 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-[#141517] transition-colors w-fit"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {appInfo.name}
            </h1>
            <span
              className={`px-2.5 py-1 min-w-max rounded-md text-[11px] font-bold ${
                appInfo.status === 1
                  ? "bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-[#00FFA3]/10 dark:text-[#00FFA3] dark:border-[#00FFA3]/20"
                  : "bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600"
              }`}
            >
              {appInfo.status === 1 ? t("common.active") : t("common.inactive")}
            </span>
          </div>
          <p className="text-slate-500 dark:text-slate-400 font-mono text-sm mt-1 tracking-tight">
            {appInfo.keyPrefix}***
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 p-6 flex items-center gap-5">
          <div className="p-4 bg-emerald-50 text-emerald-600 dark:bg-[#00FFA3]/10 dark:text-[#00FFA3] rounded-xl border border-emerald-200 dark:border-[#00FFA3]/20">
            <Activity className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mb-1">
              {t("keyDetails.statTotalRequests30d")}
            </p>
            <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              {requests30d}
            </p>
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 p-6 flex items-center gap-5">
          <div className="p-4 bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400 rounded-xl border border-orange-100 dark:border-orange-500/20">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mb-1">
              {t("keyDetails.statCreditsDeducted30d")}
            </p>
            <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              {credits30d.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 p-6 flex items-center gap-5">
          <div className="p-4 bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400 rounded-xl border border-sky-100 dark:border-sky-500/20">
            <Wallet className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mb-1">
              {t("keyDetails.statUsdtDeducted30d")}
            </p>
            <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight font-mono">
              {usdt30d === "—" ? "—" : `${formatUsdt30dStatDisplay(usdt30d)} USDT`}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
          <List className="w-5 h-5 text-slate-400 dark:text-slate-400" />
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            {t("keyDetails.usageDetailsTitle")}
          </h2>
        </div>

        <div className="overflow-x-auto custom-scrollbar relative">
          <DataTableLoadingOverlay loading={usageLoading && usageLogs.length === 0} />
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-[#0E0F11]/50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {t("keyDetails.colTime")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {t("keyDetails.colService")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                >
                  {t("keyDetails.colCalledEndpoint")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-4 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {t("keyDetails.colDeducted")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {t("keyDetails.colStatus")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-50 dark:bg-[#1a1d1f] divide-y divide-slate-200 dark:divide-slate-800">
              <DataTableStateRow
                colSpan={5}
                loading={usageLoading && usageLogs.length === 0}
                isEmpty={!usageLoading && usageLogs.length === 0}
                emptyTitle={t("keyDetails.usageDetailsEmpty")}
              />
              {usageLogs.map((log) => (
                <tr
                  key={log.id}
                  className="hover:bg-white dark:hover:bg-[#0E0F11]/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-slate-600 dark:text-slate-300">
                    {formatLogDateTime(log.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900 dark:text-slate-100">
                    {log.serviceName}
                  </td>
                  <td
                    className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 font-mono max-w-[min(420px,55vw)] truncate"
                    title={`${log.method} ${log.endpoint}`}
                  >
                    <span className="text-slate-500 dark:text-slate-500">{log.method}</span>{" "}
                    {log.endpoint}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-orange-500 dark:text-orange-400 text-right tabular-nums">
                    {log.billingType === "balance"
                      ? `${log.usdtCharged} ${t("keyDetails.unitUsdt")}`
                      : `${log.creditsCharged.toLocaleString()} ${t("keyDetails.unitCredits")}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={
                        log.success === 1
                          ? "text-emerald-600 dark:text-[#00FFA3] font-bold"
                          : "text-red-600 dark:text-red-400 font-bold"
                      }
                    >
                      {log.success === 1 ? t("common.success") : t("common.failed")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePaginationFooter
          page={usagePage}
          totalPages={usageTotalPages}
          totalItems={usageTotal}
          onPageChange={(p) => setUsagePage(p)}
          className="border-t border-slate-200 dark:border-slate-800 px-4 py-3"
        />
      </div>
    </div>
  );
}
